import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import type { FC } from 'react';
import type { Receipt, ReceiptItem } from '../data/receipt';
import { formatPercent, fullCurrencyFormatter } from '../helpers/format-currency';
import { formatInStoreTimezone } from '../helpers/format-date';
import { formatPaymentLabel } from '../helpers/format-payment';

type ReceiptCardProps = {
  receipt: Receipt;
  highlighted: boolean;
  timezone: string;
  storeId: string;
  hasException?: boolean;
};

const gridCols = 'grid grid-cols-[1fr_7rem_5rem_3rem_5rem_5rem_5.5rem_5rem]';

export const ReceiptCard: FC<ReceiptCardProps> = ({
  receipt,
  highlighted,
  timezone,
  storeId,
  hasException,
}) => {
  const date = formatInStoreTimezone(receipt.timestamp, timezone, 'yyyy-MM-dd');

  return (
    <div
      id={`receipt-${receipt.invoice_number}`}
      className={`mx-auto max-w-4xl rounded-lg border bg-white shadow-sm transition-colors duration-500 ${
        highlighted ? 'border-2 border-yellow-500' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900">
            <Link
              to="/stores/$storeId/reports/sales-journal"
              params={{ storeId }}
              search={{
                from: date,
                to: date,
                receipt: String(receipt.invoice_number),
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Invoice #{receipt.invoice_number}
            </Link>
            {hasException && (
              <Link
                to="/stores/$storeId/reports/exception-logs"
                params={{ storeId }}
                search={{ query: String(receipt.invoice_number) }}
                target="_blank"
                className="inline-flex"
                title="This receipt has linked exception log entries — click to view"
              >
                <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500 translate-y-[0.5px]" />
              </Link>
            )}
          </span>
          <span className="text-sm text-gray-500">
            {formatInStoreTimezone(receipt.timestamp, timezone, 'MMM d · h:mm a')}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          {receipt.station && <span>Station: {receipt.station}</span>}
          {receipt.initials && <span>Clerk: {receipt.initials}</span>}
        </div>
      </div>

      <div className="px-3">
        <div
          className={`${gridCols} border-b border-gray-200 py-2 text-sm font-semibold text-gray-900`}
        >
          <div>Product</div>
          <div>Barcode</div>
          <div>Size</div>
          <div className="text-right">Qty</div>
          <div className="text-right">Price</div>
          <div className="text-right">Disc.</div>
          <div className="text-right">Ext.</div>
          <div className="text-right">Margin</div>
        </div>
        {receipt.items?.map((item: ReceiptItem, index: number) => (
          <div
            key={`${item.code_num}-${index}`}
            className={`${gridCols} border-b border-gray-50 py-1 text-sm text-gray-700 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}
          >
            <div className="truncate pr-2">{item.product}</div>
            <div className="truncate">{item.barcode}</div>
            <div>{item.size}</div>
            <div className="text-right">{item.quantity}</div>
            <div className="text-right">{fullCurrencyFormatter(item.price)}</div>
            <div className={`text-right ${item.discount ? 'text-red-600' : ''}`}>
              {item.discount ? fullCurrencyFormatter(item.discount) : '-'}
            </div>
            <div className="text-right">
              {fullCurrencyFormatter((item.price - item.discount) * item.quantity)}
            </div>
            <div className="text-right">
              {item.cost
                ? formatPercent(
                    (item.price - item.discount - item.cost) / (item.price - item.discount)
                  )
                : '-'}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-500">
            {formatPaymentLabel(receipt.payment, receipt.payment2)}
          </div>
          <div className="flex gap-6">
            <div className="text-gray-500">
              Subtotal:{' '}
              <span className="font-medium text-gray-900">
                {fullCurrencyFormatter(receipt.sub_total)}
              </span>
            </div>
            <div className="text-gray-500">
              Tax:{' '}
              <span className="font-medium text-gray-900">
                {fullCurrencyFormatter(receipt.tax)}
              </span>
            </div>
            <div className="text-gray-500">
              Total:{' '}
              <span className="font-semibold text-gray-900">
                {fullCurrencyFormatter(receipt.total)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
